import {Component, computed, effect, ElementRef, inject, OnDestroy, viewChild} from '@angular/core';
import {Card} from 'primeng/card';
import {TableModule} from 'primeng/table';
import {BasicTaDetails, FullTaDetails} from '../../../api';
import {TaInfoCardComponent} from '../../components/ta-info-card/ta-info-card.component';
import {TaListBase} from '../ta-list-base.directive';
import {toTAStatusText} from '../../../util/app.types';
import {Button} from 'primeng/button';
import {Chip} from 'primeng/chip';
import {DatePipe, NgClass} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {InputText} from 'primeng/inputtext';
import {LocationService} from '../../../services/location.service';
import {TabsModule} from 'primeng/tabs';
import {AllSubmissionsSearchComponent} from '../../components/ta-list-tables/all-submissions-search/all-submissions-search.component';
import {ManagerTaListComponent} from '../../components/ta-list-tables/manager-ta-list/manager-ta-list.component';
import {ScrollHeightDirective} from '../../directives/scroll-height.directive';
import {PrepaidIndicatorComponent} from '../../components/prepaid-indicator/prepaid-indicator.component';
import {AttachmentIndicatorComponent} from '../../components/attachment-indicator/attachment-indicator.component';
import {TaCostBubbleComponent} from '../../components/ta-cost-bubble/ta-cost-bubble.component';
import {TaCommentCount} from '../../components/ta-comments/ta-comment-count';

@Component({
  selector: 'clc-manager-dashboard-page',
  imports: [
    Card,
    TableModule,
    TaInfoCardComponent,
    Button,
    Chip,
    DatePipe,
    FormsModule,
    InputText,
    NgClass,
    TabsModule,
    AllSubmissionsSearchComponent,
    ManagerTaListComponent,
    ScrollHeightDirective,
    PrepaidIndicatorComponent,
    AttachmentIndicatorComponent,
    TaCostBubbleComponent,
    TaCommentCount,
  ],
  templateUrl: './clc-manager-dashboard-page.html',
  styleUrl: './clc-manager-dashboard-page.scss'
})
export class ClcManagerDashboardPage extends TaListBase implements OnDestroy {
  private scrollContainer = viewChild<ElementRef>('scrollContainer');
  private loadMoreSentinel = viewChild<ElementRef>('loadMoreSentinel');
  private observer: IntersectionObserver | undefined;
  private loadingDebounceTimer: ReturnType<typeof setTimeout> | undefined;

  locationService = inject(LocationService);

  tabOption: string = 'myTeam';

  isLoading = computed(() => this.store.isLoadingData());
  canLoadMore = computed(() => {
    const cache = this.store.directReportTaListCache();
    if (!cache?.totalCount) return false;
    return (cache.currentCount ?? 0) < cache.totalCount;
  });

  searchTabTitle = computed(() => {
    if (this.permissionService.hasGlobalSearch()) {
      return 'Search All Submissions';
    }
    return 'Search Unit TAs';
  });

  constructor() {
    super();

    effect(() => {
      this.availableTaList.set(this.store.directReportTaListCache()?.taList ?? []);
    });

    effect(() => {
      const el = this.loadMoreSentinel()?.nativeElement;
      const root = this.scrollContainer()?.nativeElement;
      this.observer?.disconnect();
      if (el && root) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries[0].isIntersecting) {
              this.loadMore();
            }
          },
          { root, threshold: 0.1 },
        );
        this.observer.observe(el);
      }
    });

    effect(() => {
      const loading = this.isLoading();
      if (loading) {
        this.loadingDebounceTimer = setTimeout(() => {
          this.loadingService.showLoading({message: 'Loading...'});
        }, 80);
      } else {
        clearTimeout(this.loadingDebounceTimer);
        this.loadingDebounceTimer = undefined;
        this.loadingService.hideLoading();
      }
    });
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.store.updateBreadCrumbData({
      title: 'Manager Dashboard',
      titleIcon: 'fa-regular fa-user-tie fa-lg'
    });
    // A unit manager with no direct reports has no Direct Reports' TAs tab, so the default would
    // otherwise select a tab that is not rendered and leave the panel area blank.
    if (!this.permissionService.canViewDirectReportTas()) {
      this.tabOption = this.permissionService.canViewUnitTas() ? 'myUnit' : 'allSubmissions';
    }

    // Only the inline first tab loads eagerly; the rest are behind the lazy tab panels.
    if (this.permissionService.canViewDirectReportTas()) {
      this.loadTaList();
    }
    this.locationService.loadLocations();
  }

  override viewTaSubmission(taSubmission: FullTaDetails | BasicTaDetails) {
    this.store.updatePreviousPageRoute(this.router.url);
    this.router.navigate([`/ta/${taSubmission.id}/manage`]);
  }

  // The loader reuses a cache newer than LIST_CACHE_TTL_MS. Worth it here: this list is the most
  // expensive of the four, as GetDirectReportSubmissions maps submission expense per row.
  loadTaList(): void {
    this.taSearchDatasource.loadDirectReportTaList();
  }

  // The explicit refresh button, which must always hit the server.
  reloadTaList(): void {
    this.store.resetDirectReportCache();
    this.taSearchDatasource.loadDirectReportTaList();
  }

  loadMore(): void {
    if (!this.canLoadMore() || this.isLoading()) return;
    this.taSearchDatasource.loadMoreDirectReportTas();
  }

  resetSelectedTa(): void {
    this.store.resetTaState();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    clearTimeout(this.loadingDebounceTimer);
    this.observer?.disconnect();
  }

  protected readonly toTAStatusText = toTAStatusText;
}
